import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      const door = event.target.querySelector('[data-door-size]');
      if (door && !door.value) {
        this.#flagDoorSize(door);
        return;
      }

      this.#submit(event.target, event.submitter);
    }, opts);

    // Required door size: replace the browser bubble with an on-page message and bring the field into view.
    this.addEventListener('invalid', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLSelectElement) || !field.matches('[data-door-size]')) return;
      event.preventDefault();
      this.#flagDoorSize(field);
    }, { ...opts, capture: true });

    this.addEventListener('change', (event) => {
      const field = event.target;
      if (field instanceof HTMLSelectElement && field.matches('[data-upsell-door]') && field.value) {
        field.removeAttribute('aria-invalid');
        const upsellError = field.closest('[data-upsell]')?.querySelector('[data-upsell-door-error]');
        if (upsellError) upsellError.hidden = true;
      }
      if (field instanceof HTMLSelectElement && field.matches('[data-door-size]') && field.value) {
        field.removeAttribute('aria-invalid');
        const error = this.querySelector('[data-door-error]');
        if (error) error.hidden = true;
      }
    }, opts);

    // Mobile sticky bar: commandfor="product-buy" command="--buy" checks the door size, then submits through the real add button.
    this.addEventListener('command', (event) => {
      if (event.command !== '--buy') return;
      const form = this.querySelector('form');
      if (!form) return;
      const door = form.querySelector('[data-door-size]');
      if (door && !door.value) {
        this.#flagDoorSize(door);
        return;
      }
      const addButton = form.querySelector('button[name="add"][value="checkout"]') || form.querySelector('button[name="add"]');
      form.requestSubmit(addButton instanceof HTMLButtonElement ? addButton : undefined);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    const upsellLines = this.#upsellLines(form);
    if (!line || !upsellLines) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line, ...upsellLines] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // data-checkout-url (written by Liquid from routes) sends buyers straight to checkout after a successful add.
        // A submit button with value="cart" ("Sepete Ekle") keeps the buyer on the page and opens the cart instead.
        const stayInCart = submitter instanceof HTMLButtonElement && submitter.value === 'cart';
        if (this.dataset.checkoutUrl && !stayInCart) {
          window.location.assign(this.dataset.checkoutUrl);
        } else {
          await window.Shopify.actions.openCart?.();
        }
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #flagDoorSize(field) {
    field.setAttribute('aria-invalid', 'true');
    const error = this.querySelector('[data-door-error]');
    if (error) error.hidden = false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    field.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    field.focus({ preventScroll: true });
  }

  // Ticked "Bunu da almak ister misiniz?" boxes add their variant as extra cart lines next to the main product.
  // An offer box with a door-size select (activity center offered on other products) sends it as the line's "Kapı Ölçüsü"; returns null when it is missing.
  #upsellLines(form) {
    const lines = [];
    const checked = form.querySelectorAll('input[type="checkbox"][data-upsell-variant]:checked:not(:disabled)');
    for (const input of checked) {
      // Skip offers that are ticked but hidden (e.g. extra bar inside an unticked activity center offer).
      if (input.getClientRects().length === 0) continue;
      let merchandiseId = String(input.dataset.upsellVariant || '').trim();
      const box = input.closest('[data-upsell]');
      const door = box?.querySelector('[data-upsell-door]');
      if (door && !door.value) {
        this.#flagUpsellDoor(box, door);
        return null;
      }
      // Extra bar box: buyer picks 100, 120 or 150 cm; 100 uses the short bar product, 120/150 the long one, length goes on the line as "Sopa Boyu".
      if (door && input.dataset.barShort && input.dataset.barLong) {
        const digits = String(door.value).match(/\d+/);
        const size = digits ? Number.parseInt(digits[0], 10) : 0;
        merchandiseId = size > 100 ? input.dataset.barLong : input.dataset.barShort;
      }
      if (!merchandiseId) continue;
      // Extra bar boxes carry their own quantity selector; other offers add one.
      const qtyInput = [...(box?.querySelectorAll('[data-upsell-qty] input[type="number"]') || [])]
        .find((el) => el.closest('[data-upsell]') === box);
      const qtyValue = Number.parseInt(String(qtyInput?.value || '1'), 10);
      const quantity = Number.isFinite(qtyValue) && qtyValue > 0 ? qtyValue : 1;
      const line = { merchandiseId, quantity };
      if (door) line.attributes = [{ key: door.dataset.propertyKey || 'Kapı Ölçüsü', value: door.value }];
      lines.push(line);
    }
    return lines;
  }

  #flagUpsellDoor(box, door) {
    door.setAttribute('aria-invalid', 'true');
    const error = box.querySelector('[data-upsell-door-error]');
    if (error) error.hidden = false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    door.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    door.focus({ preventScroll: true });
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
